"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";

export interface FilterSidebarItem {
  key: string;
  label: string;
  badge?: string;
  count?: number;
  iconUrl?: string | null;
}

export type FilterSidebarGroup =
  | {
      kind: "select";
      items: readonly FilterSidebarItem[];
      activeKey: string | null;
      onChange: (key: string) => void;
    }
  | {
      kind: "links";
      items: readonly (FilterSidebarItem & { href: string })[];
    };

interface FilterSidebarProps {
  groups: readonly FilterSidebarGroup[];
  className?: string;
}

export function FilterSidebar({ groups, className }: FilterSidebarProps) {
  return (
    <div className={cn("hidden w-[200px] shrink-0 md:sticky md:top-4 md:block md:self-start", className)}>
      <nav className="rounded-ds-sm border-ds-border bg-ds-surface-muted shadow-ds-edge-light flex max-h-[calc(100dvh-32px)] flex-col gap-1 overflow-y-auto border p-1">
        {groups.map((group, i) => (
          <FilterSidebarGroupView key={i} group={group} showDivider={i > 0} />
        ))}
      </nav>
    </div>
  );
}

function FilterSidebarGroupView({ group, showDivider }: { group: FilterSidebarGroup; showDivider: boolean }) {
  return (
    <>
      {showDivider && <div className="bg-ds-border-default mx-[10px] my-1 h-px" aria-hidden />}
      {group.kind === "select"
        ? group.items.map(item => (
            <FilterButton
              key={item.key}
              item={item}
              isActive={group.activeKey === item.key}
              onClick={() => group.onChange(item.key)}
            />
          ))
        : group.items.map(item => <FilterLink key={item.key} item={item} />)}
    </>
  );
}

function FilterButton({
  item,
  isActive,
  onClick,
}: {
  item: FilterSidebarItem;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-ds-xs flex h-9 w-full items-center justify-between gap-2 overflow-hidden px-[10px] py-2 text-left text-sm font-medium leading-5 transition-colors",
        isActive
          ? "text-ds-text-secondary shadow-ds-elev-1 bg-ds-surface-card"
          : "text-ds-text-subtle hover:text-ds-text-secondary hover:bg-ds-surface-hover",
      )}
    >
      <FilterLabel item={item} />
      <FilterMeta item={item} />
    </button>
  );
}

function FilterLink({ item }: { item: FilterSidebarItem & { href: string } }) {
  return (
    <Link
      href={item.href}
      className="rounded-ds-xs hover:bg-ds-surface-hover text-ds-text-subtle hover:text-ds-text-secondary flex h-9 w-full items-center justify-between gap-2 px-[10px] py-2 text-sm font-medium leading-5 transition-colors"
    >
      <FilterLabel item={item} />
      <FilterMeta item={item} />
    </Link>
  );
}

function FilterLabel({ item }: { item: FilterSidebarItem }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {item.iconUrl ? (
        <img
          src={item.iconUrl}
          alt=""
          aria-hidden
          className="h-4 w-4 shrink-0 rounded-full"
          onError={e => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : null}
      <span className="truncate">{item.label}</span>
    </span>
  );
}

function FilterMeta({ item }: { item: FilterSidebarItem }) {
  if (!item.badge && item.count === undefined) return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {item.badge && (
        <span className="rounded-ds-xs bg-ds-focus-brand text-3xs px-1.5 py-0.5 font-medium uppercase text-white">
          {item.badge}
        </span>
      )}
      {item.count !== undefined && <span className="text-ds-text-muted text-xs tabular-nums">{item.count}</span>}
    </span>
  );
}
